// Settings — budget caps, retry limits, model overrides, outputs.
// Only the API-declared `editable` dot-paths are mutable; everything else
// (trigger, integrations) is rendered read-only.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../api";
import Icon from "./icons";
import Shell from "./Shell";
import { ErrorState, LoadingState, Toggle, apiErrorMessage } from "./shared";

// Per-field numeric constraints. Kept in sync with the inputs below and
// (deliberately) mirrors the server-side range validation in the API
// contract so we never round-trip an obviously-invalid PATCH that the
// service would just 422 anyway.
const FIELD_SPECS = {
  "budget.perRunCapUsd": { min: 0.5, max: 50, integer: false },
  "budget.testPlanCapUsd": { min: 0.25, max: 10, integer: false },
  "budget.maxFixerRetries": { min: 1, max: 10, integer: true },
  // Sandbox hard-caps the loop at 5 (resolveMaxReviewIterations) and the
  // server ceiling matches — accepting 6-10 here would save a knob value the
  // loop silently ignores.
  "budget.maxReviewIterations": { min: 1, max: 5, integer: true }
};

function clampToSpec(path, value) {
  const spec = FIELD_SPECS[path];
  if (!spec || value == null || Number.isNaN(value)) return value;
  let v = value;
  if (v < spec.min) v = spec.min;
  if (v > spec.max) v = spec.max;
  if (spec.integer) v = Math.round(v);
  return v;
}

// Returns a human error for an editable numeric field, or null if valid.
function fieldError(path, value) {
  const spec = FIELD_SPECS[path];
  if (!spec) return null;
  if (value == null || value === "" || Number.isNaN(value))
    return "Required — enter a number.";
  if (spec.integer && !Number.isInteger(value))
    return "Must be a whole number.";
  if (value < spec.min || value > spec.max)
    return `Must be between ${spec.min} and ${spec.max}.`;
  return null;
}

const Section = ({ title, desc, children }) => (
  <div className="bss-card" style={{ padding: "8px 24px", marginBottom: 16 }}>
    <div
      style={{
        paddingTop: 16,
        paddingBottom: 12,
        borderBottom: "1px solid var(--bss-border)"
      }}
    >
      <h2 style={{ fontSize: 14, fontWeight: 600 }}>{title}</h2>
      {desc && (
        <div
          style={{
            fontSize: 12.5,
            color: "var(--bss-fg-muted)",
            marginTop: 4
          }}
        >
          {desc}
        </div>
      )}
    </div>
    <div>{children}</div>
  </div>
);

const Row = ({ name, desc, children }) => (
  <div className="bss-setting-row">
    <div>
      <div className="bss-name">{name}</div>
      <div className="bss-desc">{desc}</div>
    </div>
    <div className="bss-ctrl">{children}</div>
  </div>
);

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .bssSettings()
      .then((res) => {
        if (!mountedRef.current) return;
        setSettings(res.data);
        setDraft(JSON.parse(JSON.stringify(res.data)));
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        setError(err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  const editable = useMemo(
    () => new Set((settings && settings.editable) || []),
    [settings]
  );
  const can = (path) => editable.has(path);

  const dirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(draft),
    [settings, draft]
  );

  const setField = (group, field, value) =>
    setDraft((d) => ({ ...d, [group]: { ...d[group], [field]: value } }));

  // Clamp a numeric field to its spec on blur so the user sees the value
  // actually being submitted (and can't leave it silently out of range).
  const clampField = (group, field) => {
    const path = `${group}.${field}`;
    setDraft((d) => {
      const current = d[group][field];
      const clamped = clampToSpec(path, current);
      if (clamped === current) return d;
      return { ...d, [group]: { ...d[group], [field]: clamped } };
    });
  };

  // Validate every EDITABLE numeric field in the current draft.
  const validationErrors = useMemo(() => {
    if (!draft) return {};
    const errs = {};
    Object.keys(FIELD_SPECS).forEach((path) => {
      if (!editable.has(path)) return;
      const [group, field] = path.split(".");
      const msg = fieldError(path, draft[group] && draft[group][field]);
      if (msg) errs[path] = msg;
    });
    return errs;
  }, [draft, editable]);

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const buildPatch = () => {
    const patch = {};
    const consider = (group, field) => {
      const path = `${group}.${field}`;
      if (!can(path)) return;
      if (settings[group][field] === draft[group][field]) return;
      patch[group] = patch[group] || {};
      patch[group][field] = draft[group][field];
    };
    [
      "perRunCapUsd",
      "testPlanCapUsd",
      "maxFixerRetries",
      "maxReviewIterations"
    ].forEach((f) => consider("budget", f));
    ["defaultModel", "plannerModel", "reviewerModel", "fixerModel"].forEach(
      (f) => consider("model", f)
    );
    consider("outputs", "pushBranchOnFail");
    return patch;
  };

  const save = () => {
    if (hasValidationErrors) {
      setSaveError("Fix the highlighted fields before saving.");
      return;
    }
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    api
      .bssUpdateSettings(patch)
      .then((res) => {
        setSettings(res.data);
        setDraft(JSON.parse(JSON.stringify(res.data)));
        setSaving(false);
        setSaved(true);
        window.setTimeout(() => {
          if (mountedRef.current) setSaved(false);
        }, 2500);
      })
      .catch((err) => {
        setSaving(false);
        setSaveError(apiErrorMessage(err, "Could not save settings."));
      });
  };

  if (loading) {
    return (
      <Shell>
        <LoadingState label="Loading settings…" />
      </Shell>
    );
  }
  if (error || !draft) {
    return (
      <Shell>
        <ErrorState message={apiErrorMessage(error)} onRetry={load} />
      </Shell>
    );
  }

  const numberInput = (group, field, opts = {}) => {
    const path = `${group}.${field}`;
    const err = can(path) ? validationErrors[path] : null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {opts.prefix && (
            <span style={{ color: "var(--bss-fg-muted)" }}>{opts.prefix}</span>
          )}
          <input
            className="bss-input"
            type="number"
            step={opts.step || 1}
            min={opts.min}
            max={opts.max}
            aria-invalid={!!err}
            disabled={!can(path)}
            style={{
              width: 90,
              textAlign: "right",
              borderColor: err ? "var(--bss-danger)" : undefined
            }}
            value={draft[group][field] != null ? draft[group][field] : ""}
            onChange={(e) =>
              setField(
                group,
                field,
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
            onBlur={() => clampField(group, field)}
          />
        </div>
        {err && (
          <span style={{ color: "var(--bss-danger)", fontSize: 11.5 }}>
            {err}
          </span>
        )}
      </div>
    );
  };

  const integrationBadge = (status) =>
    status === "connected" ? (
      <span className="bss-badge bss-badge--green">
        <span className="bss-dot" />
        Connected
      </span>
    ) : (
      <span className="bss-badge bss-badge--red">
        <span className="bss-dot" />
        Disconnected
      </span>
    );

  return (
    <Shell>
      <div className="bss-page-hd">
        <div>
          <h1>Settings</h1>
          <div className="bss-page-hd-sub">
            Workspace-wide defaults for BSS. Trigger rules and integrations are
            read-only.
          </div>
        </div>
        <div className="bss-page-hd-right">
          {saved && (
            <span style={{ fontSize: 12.5, color: "var(--bss-success)" }}>
              Saved
            </span>
          )}
          <button
            type="button"
            className="bss-btn bss-btn--ghost"
            disabled={!dirty || saving}
            onClick={() => setDraft(JSON.parse(JSON.stringify(settings)))}
          >
            Discard
          </button>
          <button
            type="button"
            className="bss-btn bss-btn--primary"
            disabled={!dirty || saving || hasValidationErrors}
            onClick={save}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {saveError && (
        <div
          style={{
            color: "var(--bss-danger)",
            fontSize: 13,
            marginBottom: 16
          }}
        >
          {saveError}
        </div>
      )}

      <Section
        title="Trigger"
        desc="How tickets get picked up by the agent. Managed via service config — read-only here."
      >
        <Row
          name="Auto-ship status"
          desc="Tickets moved to this Jira status are picked up by the agent."
        >
          <code className="bss-code-inline">
            {draft.trigger.autoShipStatus}
          </code>
        </Row>
        <Row
          name="Allowed Jira projects"
          desc="Only tickets in these project keys can trigger a run."
        >
          <code className="bss-code-inline">
            {(draft.trigger.allowedProjects || []).join(", ") || "—"}
          </code>
        </Row>
      </Section>

      <Section
        title="Budget & retries"
        desc="Hard caps to keep run cost predictable."
      >
        <Row
          name="Per-run budget cap"
          desc="A run is halted (no PR opened) when this is exceeded."
        >
          {numberInput("budget", "perRunCapUsd", {
            prefix: "$",
            step: 0.5,
            min: 0.5,
            max: 50
          })}
        </Row>
        <Row
          name="Test-plan run cap"
          desc="Cheaper cap for write-only test-plan runs."
        >
          {numberInput("budget", "testPlanCapUsd", {
            prefix: "$",
            step: 0.25,
            min: 0.25,
            max: 10
          })}
        </Row>
        <Row
          name="Max fixer attempts"
          desc="If tests keep failing, the run gives up after this many tries."
        >
          {numberInput("budget", "maxFixerRetries", {
            step: 1,
            min: 1,
            max: 10
          })}
        </Row>
        <Row
          name="Max reviewer ↔ fixer loops"
          desc="How many times the reviewer can request changes before the PR opens anyway. Sandbox hard-caps at 5."
        >
          {numberInput("budget", "maxReviewIterations", {
            step: 1,
            min: 1,
            max: 5
          })}
        </Row>
      </Section>

      <Section
        title="Model"
        desc="Which Claude model powers each phase. Leave overrides blank to use the default."
      >
        {[
          [
            "defaultModel",
            "Default model",
            "Used by every phase unless overridden."
          ],
          [
            "plannerModel",
            "Planner override",
            "Optional model for the planning phase."
          ],
          [
            "reviewerModel",
            "Reviewer override",
            "Optional model for the review phase."
          ],
          [
            "fixerModel",
            "Fixer override",
            "Optional model for the fixer phase."
          ]
        ].map(([field, label, desc]) => (
          <Row key={field} name={label} desc={desc}>
            <input
              className="bss-input"
              style={{ width: 200 }}
              disabled={!can(`model.${field}`)}
              placeholder={field === "defaultModel" ? "" : "(use default)"}
              value={draft.model[field] || ""}
              onChange={(e) =>
                setField(
                  "model",
                  field,
                  e.target.value === "" ? null : e.target.value
                )
              }
            />
          </Row>
        ))}
      </Section>

      <Section
        title="Outputs"
        desc="What gets pushed to GitHub when a run does not complete cleanly."
      >
        <Row
          name="Push branch even when the run fails"
          desc="Lets you inspect work-in-progress when a fixer or budget cap stops the run."
        >
          <Toggle
            on={!!draft.outputs.pushBranchOnFail}
            disabled={!can("outputs.pushBranchOnFail")}
            onChange={(v) => setField("outputs", "pushBranchOnFail", v)}
          />
        </Row>
        <Row
          name="PR refresh enabled"
          desc="Re-runs refresh existing PRs. Managed via service config — read-only."
        >
          <Toggle on={!!draft.outputs.prRefreshEnabled} disabled />
        </Row>
      </Section>

      <Section
        title="Integrations"
        desc="Source of tickets and target of PRs. Read-only."
      >
        <Row
          name="Jira"
          desc={
            <>
              Connected as{" "}
              <span className="bss-code-inline">
                {draft.integrations.jira.host}
              </span>
            </>
          }
        >
          {integrationBadge(draft.integrations.jira.status)}
        </Row>
        <Row
          name="GitHub"
          desc={
            <>
              Connected to{" "}
              <span className="bss-code-inline">
                {draft.integrations.github.repo}
              </span>
            </>
          }
        >
          {integrationBadge(draft.integrations.github.status)}
        </Row>
      </Section>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "var(--bss-fg-subtle)",
          fontSize: 12,
          marginTop: 8
        }}
      >
        <Icon.Info size={13} /> Every change is audited (actor, before/after).
      </div>
    </Shell>
  );
}
